import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';

import {parseDate} from 'shared/web';

import type {GridPdfExportContext} from 'features/eag-grid';
import {PdfExportError} from 'features/pdf-export';

import type {TIRE_SET_REQUEST_PDF_COLUMNS} from '../constants/TIRE_SET_REQUEST_PDF_COLUMNS';
import {tireSetRequestPdfCellsSchema} from '../types/tireSetRequestPdfCellsSchema';

const EMPTY_VALUE = '-';

/** Preserve selected request identities, even when several requests refer to the same tire set. */
export function createTireSetRequestPdfData(
  context: GridPdfExportContext,
  format: {
    dateTime: (value: Date) => string;
    integer: (value: number) => string | null;
  }
) {
  if (context.rows.length === 0) {
    throw new PdfExportError('general.notifications.noItemsSelected');
  }

  const titles = new Map(context.columns.map((column) => [column.key, column.title]));
  return context.rows.map((row) => {
    try {
      const cells = tireSetRequestPdfCellsSchema.parse(row.cells);
      const field = (key: keyof typeof cells) => {
        const label = titles.get(key);
        if (isNil(label) || label.trim().length === 0) {
          throw new Error(`Missing tire-set request PDF column: ${key}`);
        }
        const value = match(cells[key].value)
          .with(Pattern.nullish, () => null)
          .with(Pattern.number, (count) => {
            const formatted = format.integer(count);
            if (isNil(formatted) || formatted.trim().length === 0) {
              throw new Error('Tire count could not be formatted');
            }
            return formatted;
          })
          .with(Pattern.string, (text) =>
            key === 'neededAt' && text.length > 0 ? format.dateTime(parseDate(text)) : text
          )
          .otherwise((choice) => choice.label);
        return {key, label, value: isNil(value) || value.trim().length === 0 ? EMPTY_VALUE : value};
      };

      return {
        id: row.id,
        setNumber: field('setNumber'),
        requestHomeBranch: field('requestHomeBranch'),
        storageWarehouse: field('storageWarehouse'),
        storageLocation: field('storageLocation'),
        wheelNumber: field('wheelNumber'),
        neededAt: field('neededAt'),
        requestType: field('requestType'),
        requestState: field('requestState'),
        requestDestinationBranch: field('requestDestinationBranch'),
        wheelState: field('wheelState'),
        tireManufacturer: field('tireManufacturer'),
        tireCategory: field('tireCategory'),
        tireSeason: field('tireSeason'),
        tireWheel: field('tireWheel'),
      } satisfies Record<
        (typeof TIRE_SET_REQUEST_PDF_COLUMNS)[number],
        {key: string; label: string; value: string}
      > & {id: string};
    } catch (cause) {
      throw new PdfExportError(
        'module.reportDocument.labels.dataUnavailable',
        false,
        'entity.integration.labels.pleaseRefresh',
        {cause}
      );
    }
  });
}
